import { makeStyles } from '@material-ui/styles'
import { AppPlugins } from 'app/constants'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { listNetworkServiceDetails } from 'app/plugins/infrastructure/components/common/service-health/action'
import { useControlPlaneServicesCheck } from 'app/plugins/infrastructure/components/common/service-health/ControlPlaneServicesContext'
import { networkServicesSelector } from 'app/plugins/infrastructure/components/common/service-health/selector'
import { listRegions } from 'app/plugins/infrastructure/components/regions/actions'
import { regionsSelector } from 'app/plugins/infrastructure/components/regions/selectors'
import { IHealthServiceDetailsPageTabs } from 'app/plugins/openstack/components/networks/model'
import { RootState } from 'app/store'
import { setAmplitudeUser } from 'app/utils/tracking'
import { cacheStoreKey, loadingStoreKey } from 'core/caching/cacheReducers'
import DocumentMeta from 'core/components/DocumentMeta'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import BannerContainer from 'core/components/notifications/BannerContainer'
import BannerContent from 'core/components/notifications/BannerContent'
import { MessageTypes } from 'core/components/notifications/model'
import PollingData from 'core/components/PollingData'
import Progress from 'core/components/progress/Progress'
import SessionExpiryModal from 'core/components/SessionExpiryModal'
import SimpleLink from 'core/components/SimpleLink'
import { determineCurrentStack, loadRegionFeatures } from 'core/containers/AuthController'
import RedirectToSecondaryRegion from 'core/elements/header/GlobalRegionDropdown'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import pluginManager from 'core/plugins/pluginManager'
import { useToast } from 'core/providers/ToastProvider'
import { PreferencesState, preferencesStoreKey } from 'core/session/preferencesReducers'
import { sessionActions, SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { getRegionTerm, isInfraRegion, isOpenCloud } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import Watchdog from 'core/watchdog'
import sessionTimeoutCheck from 'core/watchdog/session-timeout'
import DataKeys from 'k8s/DataKeys'
import moment from 'moment'
import {
  getBlueprints,
  getHostNetworkConfigs,
  listResmgrHosts,
} from 'openstack/components/infrastructure/actions'
import {
  ControlPlaneServiceUnhealthyMessage,
  getHeaderAlertMessage,
  hasCinderRole,
  hasHypervisorRole,
  hasImageLibraryRole,
  hostUnhealthy,
} from 'openstack/components/infrastructure/helpers'
import { resmgrHostsSelector } from 'openstack/components/infrastructure/selectors'
import { keys, paths, prop } from 'ramda'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import Bugsnag from 'utils/bugsnag'

interface StyleProps {
  path?: string
  showNavBar?: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  appFrame: {
    zIndex: 1,
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    width: '100%',
    minHeight: '100vh',
    backgroundColor: theme.palette.grey['000'],
  },
  secondaryHeader: {
    position: 'fixed',
    top: 55,
    width: '100%',
    zIndex: 1100,
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  'contentShift-left': {
    marginLeft: 0,
  },
  'contentShift-right': {
    marginRight: 0,
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    minHeight: theme.spacing(6),
  },
  contentMain: {
    padding: ({ showNavBar }) =>
      showNavBar ? theme.spacing(3, 3, 3, 3.5) : theme.spacing(3, 3, 3, 25),
  },
  sandboxBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,

    fontWeight: 'bold',
    fontSize: 16,
    '& a': {
      backgroundColor: '#F3F3F4',
      margin: theme.spacing(0, 1),
      '&:hover': {
        backgroundColor: '#FFFFFF',
      },
      '& *': {
        color: theme.palette.background,
      },
      '& i': {
        marginLeft: theme.spacing(),
      },
    },
  },
  modal: {
    position: 'fixed',
    left: 0,
    top: '55px',
    width: '100vw',
    height: 'calc(100vh - 55px)', // 55px is the toolbar height
    overflow: 'auto',
    zIndex: 1500,
    backgroundColor: theme.palette.grey['100'],
    padding: theme.spacing(2, 4),
    boxSizing: 'border-box',
  },
  headerAlert: {
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: 'max-content max-content',
    gap: 8,
  },
  headerIcon: {
    color: theme.palette.red[500],
  },
  headerAlertCancel: {
    alignSelf: 'center',
    cursor: 'pointer',
    padding: theme.spacing(0, 1),
  },
  lastChecked: {
    alignSelf: 'center',
    width: 'fit-content',
    left: 0,
    color: theme.palette.grey[700],
    cursor: 'default',
    textAlign: 'center',
    padding: theme.spacing(0, 1),
  },
}))

const allClustersLoadingPaths = paths<boolean>(
  [DataKeys.Clusters, DataKeys.ImportedClusters, DataKeys.CapiClusters].map((cacheKey) => [
    cacheStoreKey,
    loadingStoreKey,
    cacheKey,
  ]),
)

export const getUnderlayInfo = (blueprintSettings, hostConfigs) => {
  const underlayType = blueprintSettings?.virtualNetworking?.underlayType
  const vnidRange = blueprintSettings?.virtualNetworking?.vnidRange || ''
  const vniRanges = vnidRange
    .split(',')
    .map((range) => range.trim())
    .map((range) => {
      const parts = range.split(':')
      return parts[0] === parts[1] ? parts[1] : `${parts[0]}-${parts[1]}`
    })
  const firstHostConfig = hostConfigs?.[0]
  const tunnelPhysnet = firstHostConfig?.tunnelingInterface
  const labelKeys = keys(firstHostConfig?.networkLabels)
  const tunnelLabel = labelKeys?.find((key) => {
    return firstHostConfig?.networkLabels?.[key] === tunnelPhysnet
  })
  return {
    type: underlayType,
    label: tunnelLabel,
    ranges: vniRanges,
  }
}

const AuthContainer = () => {
  const { history, location } = useReactRouter()
  const dispatch = useDispatch()
  const showToast = useToast()
  const { prefs, updatePrefs } = useScopedPreferences()
  const { lastStack } = prefs
  const isOpenCloudProduct = isOpenCloud()
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const {
    userDetails: { id: userId, name, displayName, role },
    features,
    onboardingRedirectToUrl,
    activeRegion,
    loadingRegion,
  } = session
  // stack is the name of the plugin (ex. kubernetes, account, developer, theme)
  const [currentStack, setStack] = useState(
    determineCurrentStack(history.location, lastStack, isOpenCloudProduct),
  )
  const [headerAlertMessage, setHeaderAlertMessage] = useState('')
  const [headerLastChecked, setHeaderLastChecked] = useState('')
  const [awaitingHeaderCheck, setAwaitingHeaderCheck] = useState(true)
  const { global = {} } = useSelector(prop<string, PreferencesState>(preferencesStoreKey))
  const { featureFlags: globalFeatureFlags } = global
  const plugins = pluginManager.getPlugins()
  const currentPlugin = plugins[currentStack]

  const PluginFrame = currentPlugin.getFrame()

  // Whether to show the header alert - initialized to true and set to false when the alert is dismissed by the user
  const showHeaderAlert = useRef(true)

  // Load regions list
  const { loading: loadingRegionList } = useListAction(listRegions, {
    initialLoadingState: true,
  })
  const regionList = useSelector(regionsSelector)
  // When only Infra region is available
  const isSingleRegionDeployment = useMemo(() => regionList?.length === 1, [regionList])

  const { loading: loadingResmgrHosts, reload: reloadResmgrHosts } = useListAction(
    listResmgrHosts,
    {
      initialLoadingState: true,
    },
  )
  const resmgrHosts = useSelector(resmgrHostsSelector)

  const { reload: reloadNetworkService } = useListAction(listNetworkServiceDetails)
  const networkServiceDetails = useSelector(networkServicesSelector)

  const {
    serviceHealthStates: controlPlaneServiceHealthStates,
    lastUpdated: controlPlaneServiceHealthStatesLastUpdated,
  } = useControlPlaneServicesCheck()

  useEffect(() => {
    // If header alert is dismissed by the user, do not show it again
    if (!showHeaderAlert.current) return

    // Check health conditions upon initialization as well as
    // on every resmgr hosts poll period if the health banner
    // is active
    if (!loadingResmgrHosts && awaitingHeaderCheck) {
      const hypervisors = resmgrHosts.filter((host) => hasHypervisorRole(host))
      const imageLibraries = resmgrHosts.filter((host) => hasImageLibraryRole(host))
      const blockStorageNodes = resmgrHosts.filter((host) => hasCinderRole(host))
      const networkNodes = networkServiceDetails
      const unhealthyHypervisors = hypervisors.filter((host) => hostUnhealthy(host))
      const unhealthyImageLibraries = imageLibraries.filter((host) => hostUnhealthy(host))
      const unhealthyBlockStorageNodes = blockStorageNodes.filter((host) => hostUnhealthy(host))
      const unhealthyNetworkNodes = networkServiceDetails.filter(
        (service) => service?.status !== 'Healthy',
      )
      const mostHypervisorsUnhealthy = hypervisors.length / 2 < unhealthyHypervisors.length
      const mostImageLibrariesUnhealthy = imageLibraries.length / 2 < unhealthyImageLibraries.length
      const mostBlockStorageNodesUnhealthy =
        blockStorageNodes.length / 2 < unhealthyBlockStorageNodes.length
      const mostNetworkNodesUnhealthy = networkNodes.length / 2 < unhealthyNetworkNodes.length
      const alertMessage = getHeaderAlertMessage({
        hypervisor: hypervisors.length && mostHypervisorsUnhealthy,
        imageLibrary: imageLibraries.length && mostImageLibrariesUnhealthy,
        blockStorage: blockStorageNodes.length && mostBlockStorageNodesUnhealthy,
        network: networkNodes.length && mostNetworkNodesUnhealthy,
      })
      const timeChecked = moment().format('h:mm:ss A')
      setHeaderLastChecked(timeChecked)
      setAwaitingHeaderCheck(false)
      setHeaderAlertMessage(alertMessage)
      return
    }

    if (controlPlaneServiceHealthStates?.unhealthy > 0) {
      setHeaderLastChecked(
        moment(new Date(controlPlaneServiceHealthStatesLastUpdated)).format('h:mm:ss A'),
      )
      setAwaitingHeaderCheck(false)
      setHeaderAlertMessage(ControlPlaneServiceUnhealthyMessage)
      return
    }

    // Reset header alert message
    setHeaderAlertMessage('')
  }, [
    loadingResmgrHosts,
    resmgrHosts,
    showHeaderAlert,
    awaitingHeaderCheck,
    networkServiceDetails,
    controlPlaneServiceHealthStates,
    controlPlaneServiceHealthStatesLastUpdated,
  ])

  useEffect(() => {
    // targetPath is set in LoginPage.tsx and GlobalRegionDropdown.tsx.
    // This is used to redirect the user to the intended destination after login.
    const target = sessionStorage.getItem('targetPath')
    if (target) {
      sessionStorage.removeItem('targetPath')
      history.push(target)
    }
  }, [])

  const isLoadingInitialData = false
  // loadingClusters ||
  // (users?.length === 0 && loadingUsers) ||
  // (globalFeatureFlags === undefined && loadingFeatureFlags) ||
  // (isNilOrEmpty(features) && loadingRegion)

  const showOnboarding = false

  const shouldShowNavbarForCurrentStack =
    currentStack !== AppPlugins.MyAccount ||
    (currentStack === AppPlugins.MyAccount && role === 'admin')

  const showNavBar = shouldShowNavbarForCurrentStack && !loadingRegion
  // (!isOnboardingTargetEnv || (isOnboardingTargetEnv && globalFeatureFlags?.isOnboarded))

  const classes = useStyles({
    path: history.location.pathname,
    showNavBar: showNavBar,
  })

  // Check for openstack plugin and then check for the hosts

  // useEffect(() => {
  //   async function loadFeatureFlags() {
  //     if (isOnboardingTargetEnv) {
  //       setLoadingFeatureFlags(true)
  //       await updateOnboardingStatus()
  //       setLoadingFeatureFlags(false)
  //     }
  //   }

  //   loadFeatureFlags()
  // }, [isOnboardingTargetEnv])

  // useEffect(() => {
  //   async function loadClusters() {
  //     if (activeRegion) {
  //       await Promise.all([
  //         listClusters.call(),
  //         isDecco(features) ? listImportedClusters.call() : Promise.resolve(),
  //         listCapiClusters.call(),
  //       ])
  //     }
  //   }

  //   loadClusters()
  // }, [activeRegion, features])

  useEffect(() => {
    async function loadBlueprintSettings() {
      if (activeRegion) {
        const blueprints = await getBlueprints()
        const blueprintSettings = blueprints?.[0]
        if (!blueprintSettings) {
          return
        }
        const virtualNetworking = !!blueprintSettings?.virtualNetworking?.enabled
        const hostConfigs = await getHostNetworkConfigs()
        const underlayNetwork = getUnderlayInfo(blueprintSettings, hostConfigs)
        dispatch(
          sessionActions.updateSession({
            virtualNetworking,
            underlayNetwork,
          }),
        )
      }
    }
    loadBlueprintSettings()
  }, [activeRegion, features])

  useEffect(() => {
    if (showOnboarding) return
    // if (shouldBackfillOnboarding) {
    //   updateGlobalFeatureFlags({ isOnboarded: true }, globalFeatureFlags)
    // }
    if (onboardingRedirectToUrl) {
      history.push(onboardingRedirectToUrl)
      dispatch(sessionActions.updateSession({ onboardingRedirectToUrl: null }))
    }
  }, [showOnboarding, globalFeatureFlags, onboardingRedirectToUrl, history])

  // Load features once on startup, other times will be handled by region selection
  useEffect(() => {
    const loadRegion = async () => {
      try {
        await loadRegionFeatures()
      } finally {
        dispatch(sessionActions.updateSession({ loadingRegion: false }))
      }
    }
    loadRegion()
  }, [])

  useEffect(() => {
    if (!location) {
      return
    }
    const newStack = determineCurrentStack(location, lastStack, isOpenCloudProduct)
    setStack(newStack)
    updatePrefs({ lastStack: newStack })
  }, [location, isOpenCloudProduct])

  useEffect(() => {
    setAmplitudeUser(userId)
    Bugsnag.setUser(userId, name, displayName)

    const cleanupCb = Watchdog.register({
      handler: sessionTimeoutCheck(showToast),
      frequency: 1000,
    })
    // Reset the interval if the session changes
    return () => {
      Bugsnag.setUser()
      cleanupCb()
    }
  }, [])

  // Add session expiration handling
  useEffect(() => {
    const handleSessionExpired = (event: CustomEvent) => {
      showToast(event?.detail?.message, MessageTypes.error, 3000, {
        vertical: 'top',
        horizontal: 'center',
      })
    }

    window.addEventListener('sessionExpired', handleSessionExpired as EventListener)

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired as EventListener)
    }
  }, [showToast])

  // If the user logs in directly to the Infra page, redirect them to the region URL
  const shouldRedirectToRegionURL = useMemo(() => {
    if (loadingRegion || loadingRegionList) {
      return false
    }
    return isInfraRegion() && !isSingleRegionDeployment && !isOpenCloud()
  }, [loadingRegion, isInfraRegion, isSingleRegionDeployment, loadingRegionList])

  // Add Zendesk widget script only for Enterprise users
  // useEffect(() => {
  //   if (!airgapped && customerTier === CustomerTiers.Enterprise && isProductionEnv) {
  //     addZendeskWidgetScriptToDomBody({ userId, displayName, email: name })
  //   }
  //   return () => {
  //     hideZendeskWidget()
  //   }
  // }, [userId, displayName, name, customerTier])

  // const renderOnboardingWizard = () => (
  //   <div id="onboarding" className={classes.modal}>
  //     <OnboardingPage initialStep={onboardingWizardStep} />
  //   </div>
  // )

  const regionTerm = getRegionTerm()

  return (
    <>
      <DocumentMeta title="Welcome" />
      {loadingRegion || isLoadingInitialData || shouldRedirectToRegionURL ? (
        // (isOnboardingTargetEnv && globalFeatureFlags?.isOnboarded === undefined) ? (
        <>
          <Progress
            loading
            message={
              shouldRedirectToRegionURL
                ? `Redirecting to ${regionTerm}...`
                : `Loading ${regionTerm} data...`
            }
          >
            {/* Redirect to secondary region if needed */}
            {shouldRedirectToRegionURL && <RedirectToSecondaryRegion hideDropdown={true} />}
          </Progress>
        </>
      ) : (
        <>
          {showHeaderAlert && headerAlertMessage && (
            <>
              {/* Poll every 10 minutes when banner is showing */}
              <PollingData
                hidden
                loading={loadingResmgrHosts}
                onReload={() => {
                  setAwaitingHeaderCheck(true)
                  reloadNetworkService(true, false)
                  return reloadResmgrHosts(true, false)
                }}
                refreshDuration={600000}
              />
              <BannerContainer />
              <BannerContent>
                <Text className={classes.lastChecked} variant="body2">
                  {/* <Tooltip message="Checked every 10 minutes">
                    <span>Last checked {headerLastChecked}</span>
                  </Tooltip> */}
                </Text>
                <div className={classes.headerAlert}>
                  <FontAwesomeIcon className={classes.headerIcon}>
                    exclamation-triangle
                  </FontAwesomeIcon>
                  <Text variant="caption1">
                    {headerAlertMessage}{' '}
                    {isAdmin() && (
                      <SimpleLink
                        src={routeManager.routes.openstack.health.details.path({
                          tab: headerAlertMessage.includes(ControlPlaneServiceUnhealthyMessage)
                            ? IHealthServiceDetailsPageTabs.ControlPlaneServicesHealth
                            : IHealthServiceDetailsPageTabs.ServiceHealth,
                        })}
                        textVariant="caption1"
                      >
                        See Details
                      </SimpleLink>
                    )}
                  </Text>
                </div>
                <FontAwesomeIcon
                  className={classes.headerAlertCancel}
                  onClick={() => {
                    showHeaderAlert.current = false
                    setHeaderAlertMessage('')
                  }}
                >
                  xmark
                </FontAwesomeIcon>
              </BannerContent>
            </>
          )}
          {/* eslint-disable-next-line no-constant-condition */}
          <PluginFrame currentPluginId={currentStack} features={features} role={role} />
        </>
      )}
      {/* Session Expiry Warning Modal */}
      <SessionExpiryModal />
    </>
  )
}

export default AuthContainer
